import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

// Conversations
export const getConversations = async (token) => {
  const { data } = await axios.get(`${API_URL}/conversations`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};

export const createOrGetConversation = async (participantId, token) => {
  const { data } = await axios.post(
    `${API_URL}/conversations`,
    { participantId },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

export const getConversation = async (conversationId, token) => {
  const { data } = await axios.get(
    `${API_URL}/conversations/${conversationId}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

export const deleteConversation = async (conversationId, token) => {
  const { data } = await axios.delete(
    `${API_URL}/conversations/${conversationId}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

export const markConversationAsUnread = async (conversationId, token) => {
  const { data } = await axios.put(
    `${API_URL}/conversations/${conversationId}/unread`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

export const muteConversation = async (conversationId, until, token) => {
  const { data } = await axios.put(
    `${API_URL}/conversations/${conversationId}/mute`,
    { until },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

// Messages
export const getMessages = async (conversationId, page, token) => {
  const { data } = await axios.get(
    `${API_URL}/messages/${conversationId}?page=${page}&limit=50`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

export const sendMessageAPI = async (messageData, token) => {
  const { data } = await axios.post(`${API_URL}/messages`, messageData, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};

export const reactToMessage = async (messageId, emoji, token) => {
  const { data } = await axios.put(
    `${API_URL}/messages/${messageId}/react`,
    { emoji },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

export const forwardMessage = async (messageId, conversationIds, token) => {
  const { data } = await axios.post(
    `${API_URL}/messages/${messageId}/forward`,
    { conversationIds },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

export const deleteMessage = async (messageId, forEveryone, token) => {
  const { data } = await axios.delete(`${API_URL}/messages/${messageId}`, {
    headers: { Authorization: `Bearer ${token}` },
    data: { forEveryone },
  });
  return data;
};

export const translateMessage = async (messageId, targetLang, token) => {
  const { data } = await axios.post(
    `${API_URL}/messages/${messageId}/translate`,
    { targetLang },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

export const searchMessages = async (conversationId, query, token) => {
  const { data } = await axios.get(
    `${API_URL}/messages/${conversationId}/search?query=${encodeURIComponent(query)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

// Groups
export const createGroup = async (groupData, token) => {
  const { data } = await axios.post(`${API_URL}/groups`, groupData, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};

export const updateGroup = async (groupId, updates, token) => {
  const { data } = await axios.put(`${API_URL}/groups/${groupId}`, updates, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};

export const addGroupMembers = async (groupId, userIds, token) => {
  const { data } = await axios.post(
    `${API_URL}/groups/${groupId}/members`,
    { userIds },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

export const removeGroupMember = async (groupId, memberId, token) => {
  const { data } = await axios.delete(
    `${API_URL}/groups/${groupId}/members/${memberId}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

export const leaveGroup = async (groupId, token) => {
  const { data } = await axios.post(
    `${API_URL}/groups/${groupId}/leave`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

export const makeGroupAdmin = async (groupId, memberId, token) => {
  const { data } = await axios.put(
    `${API_URL}/groups/${groupId}/admins/${memberId}`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

export const removeGroupAdmin = async (groupId, memberId, token) => {
  const { data } = await axios.delete(
    `${API_URL}/groups/${groupId}/admins/${memberId}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

// Group invite links
export const generateInviteLink = async (groupId, token) => {
  const { data } = await axios.post(
    `${API_URL}/groups/${groupId}/invite`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

export const revokeInviteLink = async (groupId, token) => {
  const { data } = await axios.delete(
    `${API_URL}/groups/${groupId}/invite`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

export const joinGroupViaInvite = async (inviteLink, token) => {
  const { data } = await axios.post(
    `${API_URL}/groups/join/${inviteLink}`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

// Group settings
export const updateGroupSettings = async (groupId, settings, token) => {
  const { data } = await axios.put(
    `${API_URL}/groups/${groupId}/settings`,
    settings,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

// Join requests (for groups with approval required)
export const approveJoinRequest = async (groupId, userId, token) => {
  const { data } = await axios.post(
    `${API_URL}/groups/${groupId}/requests/${userId}/approve`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

export const rejectJoinRequest = async (groupId, userId, token) => {
  const { data } = await axios.post(
    `${API_URL}/groups/${groupId}/requests/${userId}/reject`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

// Calls
export const getCallHistory = async (token) => {
  const { data } = await axios.get(`${API_URL}/calls`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};

export const deleteCallFromHistory = async (callId, token) => {
  const { data } = await axios.delete(`${API_URL}/calls/${callId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};
